import React, { useEffect, useState } from 'react'
import { addDoc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'

interface Props {
  onClose: () => void
  onCreated: () => void
}

export default function ReportModal({ onClose, onCreated }: Props) {
  const { orgId, userData, isForemanOrAbove } = useAuth()
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [projectId, setProjectId] = useState('')
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([])
  const [crewOnSite, setCrewOnSite] = useState('')
  const [workCompleted, setWorkCompleted] = useState('')
  const [workInProgress, setWorkInProgress] = useState('')
  const [equipmentUsed, setEquipmentUsed] = useState('')
  const [weather, setWeather] = useState('')
  const [delays, setDelays] = useState('')
  const [materialsIssues, setMaterialsIssues] = useState('')
  const [safetyIncidents, setSafetyIncidents] = useState('')
  const [escalate, setEscalate] = useState(false)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!orgId) return
    const q = query(
      collection(db, 'projects'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    )
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, name: d.data().name || 'Unknown' }))
      setProjects(list)
      if (list.length > 0 && !projectId) {
        setProjectId(list[0].id)
      }
    })
    return () => unsub()
  }, [orgId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) {
      setError('orgId is missing — please log out and back in')
      return
    }
    if (!projectId) {
      setError('Please select a project')
      return
    }
    setSaving(true)
    setError('')
    try {
      const selectedProject = projects.find(p => p.id === projectId)
      await addDoc(collection(db, 'organizations', orgId, 'dailyReports'), {
        orgId,
        date,
        projectId,
        projectName: selectedProject?.name || '',
        crewOnSite: Number(crewOnSite) || 0,
        workCompleted,
        workInProgress,
        equipmentUsed,
        weather,
        delays,
        materialsIssues,
        safetyIncidents,
        escalate,
        notes,
        submittedByName: userData?.name || userData?.email || 'unknown',
        submittedByUid: userData?.uid || 'unknown',
        submittedByRole: userData?.role || 'member',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Failed to create report. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-lg font-semibold text-gray-900">New Daily Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date / Project / Crew row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white" required>
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Crew on Site</label>
              <input type="number" value={crewOnSite} onChange={e => setCrewOnSite(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" placeholder="0" min="0" />
            </div>
          </div>

          {/* Work Completed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Work Completed Today</label>
            <textarea value={workCompleted} onChange={e => setWorkCompleted(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2}
              placeholder="What was completed today" />
          </div>

          {/* Work In Progress */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              Work In Progress (Not Complete)
              <span className="text-xs text-gray-400 font-normal">— flagged for escalation</span>
            </label>
            <textarea value={workInProgress} onChange={e => setWorkInProgress(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2}
              placeholder="Ongoing work not yet finished — describe status and what's needed to complete" />
          </div>

          {/* Equipment / Weather row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Used</label>
              <input type="text" value={equipmentUsed} onChange={e => setEquipmentUsed(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
                placeholder="e.g., Excavator, Crane" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weather</label>
              <input type="text" value={weather} onChange={e => setWeather(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
                placeholder="e.g., Sunny, 72°F" />
            </div>
          </div>

          {/* Delays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delays</label>
            <input type="text" value={delays} onChange={e => setDelays(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              placeholder="Any delays encountered" />
          </div>

          {/* Materials Issues */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              Materials / Equipment Issues
              <span className="text-xs text-gray-400 font-normal">— shortages, defects, delivery delays</span>
            </label>
            <textarea value={materialsIssues} onChange={e => setMaterialsIssues(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2}
              placeholder="Material shortfalls, delivery delays, broken equipment, etc." />
          </div>

          {/* Safety Incidents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              Safety Observations / Concerns
              <span className="text-xs text-gray-400 font-normal">— near misses, hazards observed, corrective actions</span>
            </label>
            <textarea value={safetyIncidents} onChange={e => setSafetyIncidents(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2}
              placeholder="Any safety observations, near misses, or hazards requiring attention" />
          </div>

          {/* Escalate checkbox */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={escalate}
                onChange={e => setEscalate(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium text-amber-800">Escalate to Superintendent</span>
                <p className="text-xs text-amber-600 mt-0.5">
                  Flag this report to be reviewed by a superintendent or above. Use when work in progress needs management attention or issues require resources.
                </p>
              </div>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2}
              placeholder="Any other information" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
              {saving ? 'Saving...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
